import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Megaphone } from 'lucide-react';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';

/**
 * Callback page for the Amazon Advertising API OAuth (LWA consent) flow. Mirrors
 * AmazonOAuthCallback (SP-API), but exchanges the Advertising `code` + `state` and signals the
 * opener tab via the `ads_oauth_complete` localStorage key.
 */
export default function AdsOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Connecting your Amazon Ads account...');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const exchangeCode = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        setStatus('error');
        setMessage('Missing authorization parameters. Please try again.');
        return;
      }

      try {
        const params = new URLSearchParams();
        params.append('code', code);
        params.append('state', state);

        const res = await apiClient.post(`${ENDPOINTS.AD_PERFORMANCE.OAUTH_CALLBACK}?${params}`);
        const data = res.data || res;

        if (data.success) {
          setStatus('success');
          setMessage('Amazon Ads account connected successfully! You can close this tab.');
          setDetails(data);
          // Signal the original tab that OAuth is complete
          localStorage.setItem('ads_oauth_complete', Date.now().toString());
        } else {
          setStatus('error');
          setMessage(data.error || 'Failed to connect Amazon Ads account.');
        }
      } catch (err) {
        setStatus('error');
        setMessage(err?.response?.data?.error || err?.message || 'Connection failed. Please try again.');
      }
    };

    exchangeCode();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFF9EE] to-slate-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        {/* Icon */}
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
          status === 'processing' ? 'bg-[#FFE8C8]' :
          status === 'success' ? 'bg-emerald-100' : 'bg-red-100'
        }`}>
          {status === 'processing' && <Loader2 size={28} className="text-[#C45500] animate-spin" />}
          {status === 'success' && <CheckCircle size={28} className="text-emerald-600" />}
          {status === 'error' && <XCircle size={28} className="text-red-600" />}
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          {status === 'processing' ? 'Connecting Amazon Ads...' :
           status === 'success' ? 'Connected!' : 'Connection Failed'}
        </h2>

        {/* Message */}
        <p className="text-sm text-gray-500 mb-6">{message}</p>

        {/* Success details */}
        {status === 'success' && details && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#FFE8C8] rounded-lg flex items-center justify-center">
                <Megaphone size={18} className="text-[#C45500]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Amazon Advertising</p>
                <p className="text-xs text-gray-400">Sponsored Ads account</p>
              </div>
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              {details.adsProfileId && (
                <div className="flex justify-between">
                  <span>Ads Profile ID</span>
                  <span className="font-medium text-gray-700">{details.adsProfileId}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-center">
          {status === 'success' && (
            <>
              <button
                onClick={() => window.close()}
                className="px-6 py-2.5 bg-[#232F3E] text-white rounded-lg text-sm font-semibold hover:bg-[#37475A] transition-colors shadow-sm"
              >
                Close Tab
              </button>
              <button
                onClick={() => navigate('/ad-performance')}
                className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Go to Ad Performance
              </button>
            </>
          )}
          {status === 'error' && (
            <>
              <button
                onClick={() => navigate('/ad-performance')}
                className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2.5 bg-[#232F3E] text-white rounded-lg text-sm font-semibold hover:bg-[#37475A] transition-colors shadow-sm"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
